import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';

interface VillabookingContextData {
  isAvailable: boolean;
  isWireTransfer: boolean;
  isSplitted: boolean;
  firstPay: number;
  firstPayerAmount: number;
  balanceAmount: number;
  totalAmount: number;
  maxValue: number;
  payers: Payer[];
  isBalanced: boolean;
  changeIsAvailable: (isAvailable: boolean) => void;
  changeIsWireTransfer: (value: boolean) => void;
  changeFirstPay: (value: number) => void;
  changeFirstPayerAmount: (value: number) => void;
  changeBalanceAmount: (value: number) => void;
  changeTotalAmount: (value: number) => void;
  changePayers: (value: Payer[]) => void;
  changeIsBalanced: (value: boolean) => void;
  toggleIsSplitted: (value?: boolean) => void
}

export const VillabookingContext = createContext<VillabookingContextData>(
  {} as VillabookingContextData,
);

type Payer = {
  email: string;
  name: string;
  amount: number;
};

export const VillabookingProvider = ({ children }: { children: ReactNode }) => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [isSplitted, setIsSplitted] = useState(false);
  const [isBalanced, setIsBalanced] = useState(false);
  const [isWireTransfer, setIsWireTransfer] = useState(false);
  const [firstPay, setFirstPay] = useState(0);
  const [firstPayerAmount, setFirstPayerAmount] = useState(0);
  const [balanceAmount, setBalanceAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [maxValue, setMaxValue] = useState(0);
  const [payers, setPayers] = useState<Payer[]>([]);

  function changeIsAvailable(isAvailable: boolean) {
    setIsAvailable(isAvailable);
  }

  function changeIsWireTransfer(value: boolean) {
    setIsWireTransfer(value);
  }

  function changeFirstPay(value: number) {
    setFirstPay(value);
  }

  function changeFirstPayerAmount(value: number) {
    setFirstPayerAmount(value);
  }

  function changeBalanceAmount(value: number) {
    setBalanceAmount(value);
  }

  function changeTotalAmount(value: number) {
    setTotalAmount(value);
  }

  function changePayers(value: Payer[]) {
    setPayers(value);
  }

  function changeIsBalanced(value: boolean) {
    setIsBalanced(value);
  }

  function toggleIsSplitted(value?: boolean) {
    setIsSplitted(prevState => value !== undefined ? value : !prevState);
  }

  useEffect(() => {
    let amount = 0;
    payers.forEach(payer => amount += payer.amount);
    changeFirstPayerAmount(totalAmount - amount)
    setMaxValue(balanceAmount - amount)
  }, [payers, totalAmount, balanceAmount])

  return (
    <VillabookingContext.Provider
      value={{
        maxValue,
        isAvailable,
        isWireTransfer,
        isSplitted,
        firstPay,
        firstPayerAmount,
        balanceAmount,
        totalAmount,
        payers,
        isBalanced,
        changeIsAvailable,
        changeIsWireTransfer,
        changeFirstPay,
        changeFirstPayerAmount,
        changeBalanceAmount,
        changeTotalAmount,
        changePayers,
        changeIsBalanced,
        toggleIsSplitted,
      }}>
      {children}
    </VillabookingContext.Provider>
  );
};

export default function useVillabooking(): VillabookingContextData {
  const context = useContext(VillabookingContext);

  if (!context) {
    throw new Error(
      'UseVillabooking must be used within an UseVillabookingProvider',
    );
  }

  return context;
}
